import { createAsyncThunk } from "@reduxjs/toolkit";

import * as configuratorAPI from "../../../api/configurator";
import * as productsAPI from "../../../api/products";
import { IConcreteBundle } from "../../../models/catalogue";
import { IProductID } from "../../../models/nominals";
import {
    IModularConfiguratorPromoCard,
    IModularConfiguratorUpsellInfoModal,
} from "../models";

/**
 * Parameters for loading concrete bundles.
 */
interface LoadConcreteBundlesParams {
    variantID: IProductID;
    selectedUpgradeID?: IProductID | null;
}

/**
 * Return type for loadConcreteBundles thunk.
 */
interface LoadConcreteBundlesResult {
    concreteBundles: readonly IConcreteBundle[];
    upsellInfoModals: readonly IModularConfiguratorUpsellInfoModal[];
    promoCards: readonly IModularConfiguratorPromoCard[];
    selectedUpgradeID: IProductID | null;
}

/**
 * Load concrete bundles, upsell info modals, and promo cards for a variant.
 *
 * This thunk fetches bundle data, upsell modals, and promo cards for a specific product variant,
 * then filters and processes them:
 * - Removes empty bundles (those with no suggested products)
 * - Deduplicates upsell info modals by ID
 * - Returns promo cards for display
 *
 * @param {LoadConcreteBundlesParams} params - The bundle loading parameters
 * @param {IProductID} params.variantID - The variant to load bundles for
 * @param {IProductID} [params.selectedUpgradeID] - Optional selected upgrade ID
 *
 * @returns {Promise<LoadConcreteBundlesResult>} Promise resolving to bundles, modals, and cards
 *
 * @example
 * // Load bundles when variant changes
 * dispatch(loadConcreteBundles({
 *     variantID: 123,
 *     selectedUpgradeID: 456
 * }));
 *
 * @sideEffects
 * - Updates `entities.concreteBundles` with filtered bundle data via extraReducers
 * - Updates `entities.upsellInfoModals` with deduplicated modals via extraReducers
 * - Updates `entities.promoCards` with promo card data via extraReducers
 * - Updates `ui.selectedUpgrade` with the selected upgrade ID via extraReducers
 */
export const loadConcreteBundles = createAsyncThunk(
    "configurator/loadConcreteBundles",
    async (
        params: LoadConcreteBundlesParams,
    ): Promise<LoadConcreteBundlesResult> => {
        const productURL = productsAPI.getProductURL(params.variantID);
        const [bundles, upsellInfoModals, promoCards] = await Promise.all([
            productsAPI.listConcreteBundles(productURL),
            configuratorAPI.getUpsellInfoModalContent(params.variantID),
            configuratorAPI.getPromoCards(params.variantID),
        ]);

        return {
            concreteBundles: bundles,
            upsellInfoModals: upsellInfoModals,
            promoCards: promoCards,
            selectedUpgradeID: params.selectedUpgradeID || null,
        };
    },
);
